/**
 * OASIS Education & Experience Timeline — typed data model.
 *
 * Unifies the former Legal Education, Other Education, and Eligibility
 * Preserving Activities sections into a single chronological timeline of
 * discriminated-union entries. Every entry shares a common spine (institution,
 * span, location, ongoing) and carries a `kind`-specific payload built from the
 * SAME leaf types the original three sections used — so no data is lost in the
 * unification and the internal review/reporting side can consume identical
 * shapes.
 *
 * Design notes:
 *  - Dates are raw ISO `YYYY-MM-DD` strings end-to-end (no Date/timezone
 *    formatting — deliberate, to avoid UTC/offset drift).
 *  - `durationMonths` on experience entries is DERIVED from start/end at the UI
 *    layer, not entered; it is persisted for the reviewer side's convenience.
 *  - Chronology, gaps, and overlaps are derived (see the UI derivers); only the
 *    entries themselves and gap explanations are persisted.
 */

import type {
	DegreeConferringLawSchool,
	IAdditionalLawSchool,
	ILawSchoolGrades,
	IGraduateLegalEducation,
	GraduationHonors,
	ClerkshipCourtType,
	ClerkshipJobTitle,
	EpaWorkSchedule,
	EpaPayment,
	FellowshipType,
	DojFellowshipType,
	ISupportingDocument,
	YesNo,
} from './oasis-application.types';

/** The discriminator across all timeline entries. */
export type TimelineKind = 'undergrad' | 'non_legal_grad' | 'initial_legal_degree' | 'graduate_legal' | 'clerkship' | 'fellowship';

/** Broad grouping used for the "Add" affordances and disclosure. */
export type TimelineGroup = 'education' | 'experience';

/** Which group each kind belongs to. */
export const TIMELINE_KIND_GROUP: Record<TimelineKind, TimelineGroup> = {
	undergrad: 'education',
	non_legal_grad: 'education',
	initial_legal_degree: 'education',
	graduate_legal: 'education',
	clerkship: 'experience',
	fellowship: 'experience',
};

/** Human labels for each kind. */
export const TIMELINE_KIND_LABEL: Record<TimelineKind, string> = {
	undergrad: 'Undergraduate Education',
	non_legal_grad: 'Non-Legal Graduate Education',
	initial_legal_degree: 'Law Degree',
	graduate_legal: 'Graduate Legal Education (LLM)',
	clerkship: 'Post-Law School Judicial Clerkship',
	fellowship: 'Post-Law School Legal Fellowship',
};

/** Location shape shared by education entries (courts/fellowships store their own). */
export interface TimelineLocation {
	city: string;
	/** State, or country when outside the U.S. */
	stateOrCountry: string;
	/** Country, when the education section distinguishes it (undergrad/grad). */
	country?: string;
}

// ── Common spine ─────────────────────────────────────────────────────────────

/**
 * Fields every timeline entry carries, regardless of kind. Kept minimal; each
 * kind adds its own typed payload below.
 */
export interface TimelineEntryBase {
	/** Stable client id — chronological position is derived from dates. */
	id: string;
	kind: TimelineKind;
	/** ISO start date, or '' while unset. */
	startDate: string;
	/** ISO end date, or '' when ongoing/expected. */
	endDate: string;
	/**
	 * True when in progress or expected (open-ended).
	 *
	 * LEGACY — nothing in the applicant UI sets this any more. Every card now
	 * requires an end date (expected or actual), including the non-legal graduate
	 * degree, which was the last holdout: OARM needs that completion date because
	 * it feeds the law-education end date behind `legal_education_epa_gap` and
	 * decides joint-degree eligibility.
	 *
	 * TODO(cleanup): retire this field once no stored entry carries `true`. It is
	 * kept for now because legacy rows have it, `directDatesValid` still honors it
	 * so those rows don't fail on load, and the server's EPA-gap walk reads it
	 * defensively (`entry.ongoing ? Date.now() : ...`). Removing it is a
	 * cross-repo type change with no functional gain — see TODO.md.
	 */
	ongoing: boolean;
	/** Optional free-text note (also used for a gap explanation surfaced here). */
	note?: string;
}

// ── Education kinds ──────────────────────────────────────────────────────────

/** Undergraduate degree (BA / BS / BSc). */
export interface UndergradEntry extends TimelineEntryBase {
	kind: 'undergrad';
	schoolName: string;
	location: TimelineLocation;
	/** 'BA' | 'BS' | other baccalaureate, per the Other Education AC. */
	degreeType: 'BA' | 'BS' | 'other_baccalaureate' | '';
	/** One or more majors, entered as badges (matches the former Other Education UX). */
	majors: string[];
	graduationHonors: GraduationHonors | '';
}

/** Non-legal graduate degree (MA / MS / MBA / PhD / Other). */
export interface NonLegalGradEntry extends TimelineEntryBase {
	kind: 'non_legal_grad';
	schoolName: string;
	location: TimelineLocation;
	degreeType: 'MA' | 'MS' | 'MBA' | 'PhD' | 'other' | '';
	/** Present when degreeType === 'other'. */
	otherDegreeText?: string;
	/** One or more fields of study / majors, entered as badges. */
	majors: string[];
	graduationHonors: GraduationHonors | '';
	/** "Part of a joint degree program recognized by my U.S. law school." */
	jointDegreeWithLaw: boolean;
}

/**
 * SLIP-Pathways law-school record attributes — a check-all-that-apply list.
 * (From the retired Law School Achievement screen; captured for SLIP-Pathways
 * applicants only.)
 */
export type SlipPathwaysAttribute =
	| 'legal_writing_instructor'
	| 'legal_writing_award'
	| 'departmental_honors_thesis'
	| 'law_review_writing_competition'
	| 'law_review_grades_only'
	| 'oral_advocacy_award';

/**
 * SLIP-Pathways-only supplement on the initial legal degree — the questions
 * from the retired Law School Achievement screen. `attributes` is the checklist;
 * the two disclosures are explicit Yes/No with no default (`''` until chosen).
 */
export interface ISlipPathwaysAchievement {
	attributes: SlipPathwaysAttribute[];
	/** "Does your transcript list any failing / non-passing grades?" */
	hasFailingGrades: YesNo | '';
	/** "Are you willing to work in Washington, D.C.?" */
	willingToWorkInDC: YesNo | '';
}

/**
 * Initial legal degree (the former Legal Education section).
 *
 * Shape mirrors how the applicant actually thinks about this: ONE school grants
 * the degree (and is the school OARM evaluates — hence the path union, the
 * grades, and the bar jurisdiction), while any earlier law schools are just a
 * name and a span. The conferral date belongs to the DEGREE, so it sits here
 * rather than on each school — which is why no "which school conferred it?" or
 * "are you a transfer student?" question is needed: the degree-granting school
 * is the one described by `degreeConferring`.
 *
 * There is deliberately no attendance END date for the conferring school: for
 * that school "last date attended" and "degree conferred" are the same event,
 * and `lawDegreeExpectedOrConferred` covers current students (the date is simply
 * in the future).
 */
export interface InitialLegalDegreeEntry extends TimelineEntryBase {
	kind: 'initial_legal_degree';
	/** "Do you have / are you enrolled in a J.D. program?" 'no' → Non-J.D. path. */
	hasJd: YesNo;
	degreeConferring: DegreeConferringLawSchool;
	/** When the applicant started at the degree-granting school. */
	degreeSchoolStartDate: string;
	/** "Date Law Degree Expected / Conferred" — expected when still enrolled. */
	lawDegreeExpectedOrConferred: string;
	/** Law schools attended that did NOT confer the degree (transfer origins). */
	additionalLawSchools: IAdditionalLawSchool[];
	grades: ILawSchoolGrades;
	/**
	 * SLIP-Pathways-only supplement (retired Law School Achievement questions).
	 * Absent for HP and SLIP-Law-Clerk applicants.
	 */
	slipPathwaysAchievement?: ISlipPathwaysAchievement;
}

/**
 * Graduate legal education (LLM/SJD). Reuses `IGraduateLegalEducation` (US vs
 * non-US path, status selection, attendance). Entered once here; the system
 * treats it as both graduate legal education and — when it post-dates the
 * initial legal degree — an eligibility-preserving activity.
 */
export interface GraduateLegalEntry extends TimelineEntryBase {
	kind: 'graduate_legal';
	graduateLegalEducation: IGraduateLegalEducation;
	/**
	 * Applicant's explanation when this LLM counts as an EPA and ran outside one
	 * academic year (< 9 or > 12 months). Only collected in that case; joint-degree,
	 * concurrent, and part-time LLMs are not EPAs and are never asked.
	 */
	reasonDurationOutOfRange?: string;
}

// ── Experience kinds (Eligibility Preserving Activities) ─────────────────────

/** Judicial clerkship. Court cascade via `courtType` + `courtName`. */
export interface ClerkshipEntry extends TimelineEntryBase {
	kind: 'clerkship';
	/** Derived from start/end (rounded up); persisted for the reviewer side. */
	durationMonths: number;
	/** Present only when duration < 12 months. */
	reasonUnder12Months?: string;
	workSchedule: EpaWorkSchedule;
	courtType: ClerkshipCourtType | '';
	/** Specific court; U.S. District Court values carry the standard prefix. */
	courtName: string;
	city: string;
	stateOrCountry: string;
	jobTitle: ClerkshipJobTitle | '';
	judgeName: string;
	judgeTitle: string;
	/** `''` until answered — see the note on `IEmploymentEntry.payment`. */
	payment: EpaPayment | '';
	dutiesResponsibilities: string;
}

/** Legal fellowship. DOJ sub-type vs. the non-DOJ conditional block. */
export interface FellowshipEntry extends TimelineEntryBase {
	kind: 'fellowship';
	durationMonths: number;
	reasonUnder12Months?: string;
	workSchedule: EpaWorkSchedule;
	/** `''` until answered — see the note on `IEmploymentEntry.payment`. */
	payment: EpaPayment | '';
	fellowshipType: FellowshipType | '';
	/** Set only when fellowshipType === 'doj_fellowship'. */
	dojFellowshipType?: DojFellowshipType;

	// Non-DOJ conditional fields
	fellowshipName?: string;
	sponsorOrganization?: string;
	sponsorIsLawFirm?: YesNo;
	missionPurpose?: string;
	eligibilityUrl?: string;
	attachments?: ISupportingDocument[];
	salaryStipend?: string;

	city: string;
	stateOrCountry: string;
	supervisorName: string;
	supervisorTitle: string;
	dutiesResponsibilities: string;
}

/** The timeline entry discriminated union. */
export type TimelineEntry = UndergradEntry | NonLegalGradEntry | InitialLegalDegreeEntry | GraduateLegalEntry | ClerkshipEntry | FellowshipEntry;

/**
 * A detected chronological gap the applicant is asked to explain. Derived from
 * the sorted timeline; only `explanation` is persisted (keyed by bounding ids).
 */
export interface TimelineGap {
	afterEntryId: string;
	beforeEntryId: string;
	fromDate: string;
	toDate: string;
	months: number;
	explanation: string;
}

/**
 * The persisted timeline slice on the application body. The single source of
 * truth for legal education, other education, and eligibility-preserving
 * activities — it replaced the separate `legalEducation`, `otherEducation`, and
 * `eligibilityActivities` fields, which were removed once the timeline passed
 * UAT.
 */
export interface IEducationExperienceTimeline {
	entries: TimelineEntry[];
	/** Applicant explanations for detected gaps. */
	gapExplanations: TimelineGap[];
}
